// =============================================
// LinkFixPreviewModal — podgląd "z czego na co" PRZED zatwierdzeniem
// naprawy martwych linków wewnętrznych w opublikowanym artykule.
// =============================================
//
// Per-artykuł (batch deferred — MVP). Pokazuje listę dead links z 3 typów:
//  - slug_mismatch: kanoniczny URL (replace_href)
//  - pending_target: marker (replace_with_marker)
//  - orphan z dopasowaniem (>= 0.75 jaccard): replace_href
//  - orphan bez dopasowania: <span>{anchor}</span> (strip_to_text)
//
// Per link checkbox: domyślnie ZAZNACZONE tylko fixy z PEWNĄ sugestią
// (slug_mismatch, pending_target, orphan z matchem). Orphan-bez-matcha
// (strip_to_text) → domyślnie ODZNACZONY (user decyduje świadomie).
//
// "Zastosuj zaznaczone" → applyLinkFixes (atomicy DB+WP z rollback).

import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Link2, AlertCircle, Loader2, Wand2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  scanDeadLinksForArticle,
  type DeadLink,
  type DeadLinkScan,
} from "@/services/audit/deadLinkDetector";
import { applyLinkFixes } from "@/services/audit/applyLinkFixes";
import { isValidManualUrl } from "@/lib/manualUrlValidator";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string;
  mapId: string;
  /** Wywoływane po SUCCESS apply (do invalidate scan cache w parent). */
  onApplied?: () => void;
}

const TYPE_LABELS: Record<DeadLink["type"], string> = {
  slug_mismatch: "Rozjazd slug",
  pending_target: "Cel niepublikowany",
  orphan: "Sierota",
};

const TYPE_COLORS: Record<DeadLink["type"], string> = {
  slug_mismatch: "border-amber-500/40 bg-amber-500/5",
  pending_target: "border-blue-500/40 bg-blue-500/5",
  orphan: "border-rose-500/40 bg-rose-500/5",
};

const ACTION_LABELS: Record<NonNullable<DeadLink["suggestedAction"]>, string> =
  {
    replace_href: "Podmień href",
    replace_with_marker: "Zamień na marker pending",
    strip_to_text: "Strip do tekstu",
  };

export function LinkFixPreviewModal({
  open,
  onOpenChange,
  nodeId,
  mapId,
  onApplied,
}: Props) {
  const { toast } = useToast();
  const [scan, setScan] = useState<DeadLinkScan | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Manual URL override per link (idx → user input). Valid → auto-toggle checkbox + override suggestedHref.
  const [manualUrls, setManualUrls] = useState<Map<number, string>>(new Map());

  // Fetch scan po open
  useEffect(() => {
    if (!open || !nodeId || !mapId) return;
    let cancelled = false;
    setLoading(true);
    setScan(null);
    setSelected(new Set());
    setManualUrls(new Map());
    scanDeadLinksForArticle(nodeId, mapId)
      .then((s) => {
        if (cancelled) return;
        setScan(s);
        // Default selection: tylko fixy z PEWNĄ sugestią (NIE strip_to_text orphan)
        if (s) {
          const initial = new Set<number>();
          s.deadLinks.forEach((dl, idx) => {
            if (
              dl.suggestedAction &&
              dl.suggestedAction !== "strip_to_text"
            ) {
              initial.add(idx);
            }
          });
          setSelected(initial);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        toast({
          title: "Scan nie powiódł się",
          description: e instanceof Error ? e.message : String(e),
          variant: "destructive",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, nodeId, mapId, toast]);

  const toggleSelect = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleManualUrlChange = (idx: number, value: string) => {
    setManualUrls((prev) => {
      const next = new Map(prev);
      if (value.trim()) next.set(idx, value);
      else next.delete(idx);
      return next;
    });
    // Auto-toggle: valid URL → zaznacz wiersz (user explicit chce ten fix)
    if (isValidManualUrl(value)) {
      setSelected((prev) => {
        if (prev.has(idx)) return prev;
        const next = new Set(prev);
        next.add(idx);
        return next;
      });
    }
  };

  // selectedFixes — override suggestedHref user URL'em gdy podany i valid.
  // applyLinkFixes (atomic DB+WP) dostaje standardowy DeadLink[], cała mechanika bez zmian.
  const selectedFixes = useMemo<DeadLink[]>(() => {
    if (!scan) return [];
    const out: DeadLink[] = [];
    scan.deadLinks.forEach((dl, idx) => {
      if (!selected.has(idx)) return;
      const manual = manualUrls.get(idx);
      if (manual && isValidManualUrl(manual)) {
        out.push({
          ...dl,
          suggestedAction: "replace_href",
          suggestedHref: manual.trim(),
        });
      } else {
        out.push(dl);
      }
    });
    return out;
  }, [scan, selected, manualUrls]);

  // Guard: zaznaczone wiersze z manual URL który jest INVALID (user wpisał coś złego)
  // → button "Zastosuj" disabled (uniknij apply z niewalidowanym URL).
  const hasInvalidManualSelected = useMemo(() => {
    if (!scan) return false;
    for (const idx of selected) {
      const manual = manualUrls.get(idx);
      if (manual && !isValidManualUrl(manual)) return true;
    }
    return false;
  }, [scan, selected, manualUrls]);

  const handleApply = async () => {
    if (selectedFixes.length === 0) return;
    setApplying(true);
    try {
      const r = await applyLinkFixes(nodeId, mapId, selectedFixes);
      if (r.dbUpdated && r.wpUpdated) {
        toast({
          title: `Naprawiono ${r.fixedCount} linków`,
          description: `DB + WP zsynchronizowane${r.notMatchedCount > 0 ? ` (${r.notMatchedCount} stale — odśwież)` : ""}`,
        });
        onApplied?.();
        onOpenChange(false);
      } else if (r.rolledBack) {
        toast({
          title: "WP fail — rollback wykonany",
          description: r.error,
          variant: "destructive",
        });
      } else if (r.dbUpdated && !r.wpUpdated) {
        toast({
          title: "DB zapisane, WP NIE",
          description: r.error || "wp_post_id null lub WP padło",
          variant: "destructive",
        });
        onApplied?.();
      } else {
        toast({
          title: "Apply nie powiódł się",
          description: r.error || "unknown",
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({
        title: "Apply threw",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setApplying(false);
    }
  };

  const counts = scan?.counts;

  return (
    <Dialog open={open} onOpenChange={(o) => !applying && onOpenChange(o)}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-amber-500" />
            Naprawa linków wewnętrznych
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              <div className="text-sm">
                Artykuł:{" "}
                <span className="font-medium text-foreground">
                  {scan?.sourceTitle || "..."}
                </span>
              </div>
              {counts && counts.total > 0 && (
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span>Łącznie martwych:</span>
                  <Badge variant="outline" className="text-[10px]">
                    {counts.total}
                  </Badge>
                  {counts.slug_mismatch > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-amber-500/40 text-amber-600"
                    >
                      {counts.slug_mismatch} rozjazd
                    </Badge>
                  )}
                  {counts.pending_target > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-blue-500/40 text-blue-600"
                    >
                      {counts.pending_target} pending
                    </Badge>
                  )}
                  {counts.orphan > 0 && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-rose-500/40 text-rose-600"
                    >
                      {counts.orphan} sierota
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-2 py-2">
          {loading && (
            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Skanuję linki...</span>
            </div>
          )}

          {!loading && scan && scan.deadLinks.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <Wand2 className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Brak martwych linków w tym artykule.
            </div>
          )}

          {!loading &&
            scan &&
            scan.deadLinks.map((dl, idx) => (
              <div
                key={idx}
                className={`rounded-md border p-3 ${TYPE_COLORS[dl.type]}`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selected.has(idx)}
                    onCheckedChange={() => toggleSelect(idx)}
                    disabled={!dl.suggestedAction || applying}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">
                        {TYPE_LABELS[dl.type]}
                      </Badge>
                      {dl.similarity != null && (
                        <Badge variant="outline" className="text-[10px]">
                          jaccard {dl.similarity}
                        </Badge>
                      )}
                      {dl.suggestedAction && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] font-normal"
                        >
                          → {ACTION_LABELS[
                            // Manual URL valid → effective action = replace_href
                            isValidManualUrl(manualUrls.get(idx) || "")
                              ? "replace_href"
                              : dl.suggestedAction
                          ]}
                        </Badge>
                      )}
                      {isValidManualUrl(manualUrls.get(idx) || "") && (
                        <Badge
                          variant="outline"
                          className="text-[10px] border-violet-500/40 text-violet-600 dark:text-violet-300"
                        >
                          ✏ override manualny
                        </Badge>
                      )}
                    </div>

                    <div className="text-xs">
                      <span className="text-muted-foreground">PRZED: </span>
                      <code className="px-1 py-0.5 rounded bg-background border text-[11px] break-all">
                        {dl.href}
                      </code>
                    </div>

                    <div className="text-xs">
                      <span className="text-muted-foreground">PO: </span>
                      <code className="px-1 py-0.5 rounded bg-background border text-[11px] break-all">
                        {renderPreviewAfter(dl, manualUrls.get(idx))}
                      </code>
                    </div>

                    {dl.suggestedTitle && (
                      <div className="text-[11px] text-muted-foreground italic">
                        Cel:{" "}
                        <span className="text-foreground">
                          {dl.suggestedTitle}
                        </span>
                      </div>
                    )}

                    <div className="text-[11px] text-muted-foreground flex items-start gap-1">
                      <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>
                        anchor:{" "}
                        <span className="text-foreground">{dl.anchor}</span> ·{" "}
                        {dl.reason}
                      </span>
                    </div>

                    {/* Ręczny URL — override sugestii (zwłaszcza dla sierot bez auto-matcha) */}
                    <div className="flex items-center gap-2 pt-1.5 border-t border-dashed border-border/40">
                      <label className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
                        Ręczny URL:
                      </label>
                      <input
                        type="text"
                        value={manualUrls.get(idx) ?? ""}
                        onChange={(e) =>
                          handleManualUrlChange(idx, e.target.value)
                        }
                        placeholder="https://domena.pl/wlasciwy-wpis/  lub  /slug"
                        className="flex-1 px-2 py-1 text-[11px] rounded border border-border bg-background min-w-0"
                        disabled={applying}
                      />
                      {(() => {
                        const m = manualUrls.get(idx);
                        if (!m) return null;
                        if (isValidManualUrl(m)) {
                          return (
                            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                              ✓ override
                            </span>
                          );
                        }
                        return (
                          <span className="text-[10px] text-rose-600 dark:text-rose-400 whitespace-nowrap">
                            format? (https://… lub /slug)
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="flex-1 text-xs text-muted-foreground">
            {scan && scan.deadLinks.length > 0 && (
              <>
                Zaznaczone: <strong>{selected.size}</strong> /{" "}
                {scan.deadLinks.length}
                {scan.wpPostId == null && (
                  <span className="ml-2 text-amber-600">
                    · UWAGA: brak wp_post_id (DB zapisze, WP NIE)
                  </span>
                )}
              </>
            )}
          </div>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={applying}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleApply}
            disabled={
              applying ||
              selected.size === 0 ||
              !scan ||
              scan.deadLinks.length === 0 ||
              hasInvalidManualSelected
            }
            className="bg-amber-600 hover:bg-amber-700"
            title={
              hasInvalidManualSelected
                ? "Popraw ręczne URL (zaznaczony wiersz ma niewalidowane URL)"
                : undefined
            }
          >
            {applying ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Stosuję...
              </>
            ) : (
              `Zastosuj zaznaczone (${selected.size})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function renderPreviewAfter(dl: DeadLink, manualUrl?: string): string {
  // Manual URL valid → override (zawsze replace_href z user-podanym URL)
  if (manualUrl && isValidManualUrl(manualUrl)) {
    return manualUrl.trim();
  }
  switch (dl.suggestedAction) {
    case "replace_href":
      return dl.suggestedHref || "(brak sugestii)";
    case "replace_with_marker":
      return `<span data-pending-link="/${dl.suggestedSlug}">...</span>`;
    case "strip_to_text":
      return `<span>${dl.anchor}</span>`;
    default:
      return "(brak sugestii — wpisz ręczny URL lub odznacz)";
  }
}
